import type {
  FsnImportAttempt,
  FsnImportEstimate,
  FsnImportProductOptions,
  FsnImportSection,
} from './fsn-imports.types'

/** A choice in one of the product scope pickers. */
export interface FsnImportOption<TValue extends string> {
  value: TValue
  label: string
  description: string
}

export type FsnImportFlowStepKey =
  | 'client'
  | 'products'
  | 'fulfillment'
  | 'relationships'
  | 'addons'
  | 'notifications'
  | 'website'
  | 'preview'
  | 'status'

export interface FsnImportFlowStep {
  key: FsnImportFlowStepKey
  label: string
  title: string
  description: string
}

export const defaultFsnImportSections: FsnImportSection[] = [
  'products',
  'location',
  'delivery_areas',
  'customers',
  'reviews',
  'add_ons',
  'notification_contacts',
  'social_links',
  'logo',
]

export const defaultFsnProductOptions: FsnImportProductOptions = {
  fsn_product_scope: 'current_pages',
  custom_product_scope: 'all',
  create_current_page_collections: true,
  add_searchable_details: true,
}

export const fsnProductScopeOptions: FsnImportOption<
  FsnImportProductOptions['fsn_product_scope']
>[] = [
  {
    value: 'current_pages',
    label: 'Products on active pages',
    description: 'The safer default. Imports products attached to the current storefront pages.',
  },
  {
    value: 'all_active',
    label: 'All active',
    description:
      'Use when the florist wants the full active FSN catalog. Off-page FSN products import hidden.',
  },
  {
    value: 'none',
    label: 'None',
    description: 'Do not import FSN catalog products.',
  },
]

export const customProductScopeOptions: FsnImportOption<
  FsnImportProductOptions['custom_product_scope']
>[] = [
  {
    value: 'all',
    label: 'All',
    description: 'Imports every active florist-created product. Off-page products import hidden.',
  },
  {
    value: 'current_pages',
    label: 'Products on active pages',
    description: 'Imports only custom products attached to the current storefront pages.',
  },
  {
    value: 'none',
    label: 'None',
    description: 'Do not import florist-created custom products.',
  },
]

// The first step is the fallback for an unknown key.
export const fsnImportFlowSteps: [FsnImportFlowStep, ...FsnImportFlowStep[]] = [
  {
    key: 'client',
    label: 'Client',
    title: 'Choose the FSN client',
    description:
      'Search by florist name or FSN client ID. Once selected, we load counts and logo details to help confirm the match.',
  },
  {
    key: 'products',
    label: 'Products',
    title: 'Choose product coverage',
    description:
      'Choose how broad the product import should be. Hidden products stay off the storefront until reviewed.',
  },
  {
    key: 'fulfillment',
    label: 'Fulfillment',
    title: 'Import shop and delivery settings',
    description:
      'Bring over the main location, pickup and delivery flags, cutoff timing, and FSN delivery fees.',
  },
  {
    key: 'relationships',
    label: 'Customers',
    title: 'Import customers and reviews',
    description:
      'Bring over customer records from FSN orders and customer-authored review history.',
  },
  {
    key: 'addons',
    label: 'Add-ons',
    title: 'Import add-on products',
    description:
      'Create add-on products and sort them into Sympathy and Non-sympathy add-on collections.',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    title: 'Import notification contacts',
    description:
      'Bring over email, phone, text, and fax contacts as enabled but unverified recipients.',
  },
  {
    key: 'website',
    label: 'Website',
    title: 'Import website details',
    description: 'Use FSN social links and logo only where the current website design is blank.',
  },
  {
    key: 'preview',
    label: 'Preview',
    title: 'Review the import plan',
    description:
      'Review counts and samples before starting. Nothing changes until you start import.',
  },
  {
    key: 'status',
    label: 'Status',
    title: 'Import status',
    description: 'Watch progress and any warnings after the import starts.',
  },
]

const flowStepKeys = fsnImportFlowSteps.map((step) => step.key)

export function getFsnImportOptionDescription<TValue extends string>(
  options: readonly FsnImportOption<TValue>[],
  value: TValue
): string | null {
  return options.find((option) => option.value === value)?.description ?? null
}

export function getFsnImportFlowStep(key: FsnImportFlowStepKey): FsnImportFlowStep {
  return fsnImportFlowSteps.find((step) => step.key === key) ?? fsnImportFlowSteps[0]
}

export function getNextFsnImportStepKey(currentStep: FsnImportFlowStepKey): FsnImportFlowStepKey {
  const currentIndex = flowStepKeys.indexOf(currentStep)

  return flowStepKeys[Math.min(flowStepKeys.length - 1, currentIndex + 1)] ?? 'client'
}

export function getPreviousFsnImportStepKey(
  currentStep: FsnImportFlowStepKey
): FsnImportFlowStepKey {
  const currentIndex = flowStepKeys.indexOf(currentStep)

  return flowStepKeys[Math.max(0, currentIndex - 1)] ?? 'client'
}

export function getFsnImportSectionEstimate(
  estimate: FsnImportEstimate | null | undefined,
  section: FsnImportSection
): number {
  if (!estimate || estimate.status !== 'ready') {
    return 0
  }

  if (section === 'notification_contacts') {
    return estimate.notification_contacts?.total ?? 0
  }

  if (section === 'location') {
    return estimate.location?.available ? 1 : 0
  }

  if (section === 'delivery_areas') {
    return (
      (estimate.delivery_areas?.cities ?? 0) +
      (estimate.delivery_areas?.postal_codes ?? 0) +
      (estimate.delivery_areas?.distance_ranges ?? 0)
    )
  }

  if (section === 'customers') {
    return estimate.customers?.total ?? 0
  }

  if (section === 'reviews') {
    return estimate.reviews?.total ?? 0
  }

  if (section === 'add_ons') {
    return estimate.add_ons?.total ?? 0
  }

  if (section === 'social_links') {
    return estimate.social_links?.total ?? 0
  }

  if (section === 'logo') {
    return estimate.logo?.available ? 1 : 0
  }

  return 0
}

export function getFsnDeliveryAreaPreviewTotal(
  summary: { cities?: number; postal_codes?: number } | null | undefined = {}
): number {
  return Number(summary?.cities ?? 0) + Number(summary?.postal_codes ?? 0)
}

export interface FsnProductImpact {
  total: number
  fsn: number
  custom: number
  hiddenFsn: number
  hiddenCustom: number
}

export function getEstimatedFsnProductImpact({
  estimate,
  productOptions,
  productsSelected,
}: {
  estimate: FsnImportEstimate | null | undefined
  productOptions: Pick<FsnImportProductOptions, 'fsn_product_scope' | 'custom_product_scope'>
  productsSelected: boolean
}): FsnProductImpact {
  if (!estimate || estimate.status !== 'ready' || !productsSelected) {
    return {
      total: 0,
      fsn: 0,
      custom: 0,
      hiddenFsn: 0,
      hiddenCustom: 0,
    }
  }

  const fsnProductCount =
    productOptions.fsn_product_scope === 'none'
      ? 0
      : productOptions.fsn_product_scope === 'all_active'
        ? estimate.products.fsn.all_active
        : estimate.products.fsn.current_pages
  const customProductCount =
    productOptions.custom_product_scope === 'none'
      ? 0
      : productOptions.custom_product_scope === 'all'
        ? estimate.products.custom.all
        : estimate.products.custom.current_pages
  const hiddenFsn =
    productOptions.fsn_product_scope === 'all_active' ? estimate.products.fsn.off_page : 0
  const hiddenCustom =
    productOptions.custom_product_scope === 'all' ? estimate.products.custom.off_page : 0

  return {
    total: fsnProductCount + customProductCount,
    fsn: fsnProductCount,
    custom: customProductCount,
    hiddenFsn,
    hiddenCustom,
  }
}

export function createFsnImportAttemptId(): string {
  const browserCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : null

  if (typeof browserCrypto?.randomUUID === 'function') {
    return browserCrypto.randomUUID()
  }

  const bytes = new Uint8Array(16)

  if (typeof browserCrypto?.getRandomValues === 'function') {
    browserCrypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

export function isTerminalFsnImportAttempt(
  attempt: Pick<FsnImportAttempt, 'status'> | null | undefined
): boolean {
  return attempt?.status === 'completed' || attempt?.status === 'failed'
}
